import './HomeQuoteSummaryPage.scss';

import { Button, ButtonGroup, Title } from '@boi/core/lib/index.js';
import { withFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';

import AnimatedSpinner from '../../components/AnimatedSpinner/AnimatedSpinner.js';
import MobileBackButton from '../../components/MobileBackButton/MobileBackButton.js';
import {
  commonFormStylesIdentifier,
  commonPageStylesIdentifier,
  HOME_QUOTE_STORAGE,
  HOME_QUOTE_THANK_YOU_STORAGE,
  TOGGLE_OPTIONAL_EXTRAS
} from '../../constants';
import mortgageProviders from '../../constants/dropdowns/mortgageProviders';
import {
  EXCESS_AMOUNT_MAPPER_REVERSE,
  HOME_TYPE_VALUE,
  OPTION_TITLE_VALUES_CONSTANT,
  TITLES_CONSTANT
} from '../../constants/home.js';
import { ABOUT_YOU } from '../../constants/insuranceTypeConstants';
import { ITB_VALUE, LAST_PAGE_NAME } from '../../constants/sessionStorage/genericStorageIdentifiers';
import {
  AGENT_SUMMARY_QUOTE_STORAGE,
  HOME_RESUME_QUOTE,
  HOME_RESUME_QUOTE_OBJ
} from '../../constants/sessionStorage/homeStorageIdentifiers';
import {
  AGENT_CLICKED_REFERENCE_QUOTES,
  AGENT_CLICKED_REFERENCE_QUOTES_POLICY_CODE
} from '../../constants/sessionStorage/userStorageIdentifiers.js';
import {
  getItemFromLocalStorage,
  getItemFromSessionStorage,
  isAgent,
  isFieldError,
  removeFromSessionStorage,
  saveInSessionStorage
} from '../../helpers';
import { getRequestBuilder } from '../../helpers/RequestHelper';
import { routes } from '../../routes';
import { getLatestQuoteForPolicyCode } from '../../services/common/commonServices';
import { getLatestUnpurchasedHomeQuote } from '../../services/latestHomeQuoteService.js';
import { getFormattedCurrencyNumber, getFormattedDecimalNumber, isNotEmpty, isTrue } from '../../utils/utils';
import {
  aboutYouObjInitialValues,
  yourCoverObjInitialValues,
  yourHomeObjInitialValues
} from '../GetQuotePage/InitalValues.js';
import SummaryAccordion from './SummaryAccordion.js';

function getMonetaryValue(amount) {
  return amount ? amount : '0';
}

function validateDayRange(day) {
  if (day < 1 || day > 31) throw new Error(`Invalid day: ${day}`);
}

function validateMonthRange(month) {
  if (month < 1 || month > 12) throw new Error(`Invalid Month: ${month}`);
}

function validateDayMonth(day, month) {
  validateDayRange(day);
  validateMonthRange(month);
}

function validateDateArrayLength(startDateSplit, startDate) {
  if (startDateSplit.length !== 3) throw new Error(`Invalid date format: ${startDate}`);
}

function getPolicyStart(startDate) {
  const startDateSplit = startDate.split('/');
  validateDateArrayLength(startDateSplit, startDate);

  const [dayStr, monthStr, yearStr] = startDateSplit;
  const day = Number(dayStr);
  const month = Number(monthStr);
  const year = Number(yearStr);
  validateDayMonth(day, month);

  return new Date(year, month - 1, day).toISOString().split('T')[0];
}

// eslint-disable-next-line complexity
const HomeQuoteSummaryPage = props => {
  const {
    history,
    errors,
    values,
    isValid,
    submitForm,
    setFieldValue,
    touched
  } = props;

  const className = 'p-HomeQuoteSummaryPage';

  const [invalidStartDate, setInvalidStartDate] = useState(false);

  let emailParam;
  let policyCodeParam;

  if (isAgent() && getItemFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES)) {
    emailParam = getItemFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES);
    policyCodeParam = getItemFromSessionStorage(AGENT_CLICKED_REFERENCE_QUOTES_POLICY_CODE);
  }

  const [quoteDetails, setQuoteDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [aboutYouObj, setAboutYou] = useState(
    Object.assign({}, { ...aboutYouObjInitialValues, policyCode: '' })
  );
  const [yourHomeObj, setYourHome] = useState(
    Object.assign({}, yourHomeObjInitialValues)
  );
  const [yourCoverObj, setYourCover] = useState(
    Object.assign({}, {
      ...yourCoverObjInitialValues,
      policyStartDate: '',
      excessAmount: 0,
      contentsAccidentalDamageCover: false,
      noClaimDiscountCover: false,
      homeEmergencyAssistCover: false
    })
  );

  function agentSearchResultSelected() {
    return emailParam && policyCodeParam;
  }

  useEffect(() => {
    if (isAgent() && agentSearchResultSelected()) {
      getQuoteDataForAgent(emailParam, policyCodeParam);
    }
  }, []);

  useEffect(() => {
    var itb = yourCoverObj.itb;
    setFieldValue('itbQuoteAssumptions', itb);
    setFieldValue('areAssumptionsCorrect', itb === true ? true : null);
  }, [yourCoverObj]);

  const loadAboutYou = (object, override) => {
    const replaceOrKeepValue = key =>
      key in object && object[key] !== '' ? object[key] : aboutYouObj[key];
    setAboutYou(
      Object.keys(aboutYouObj).reduce((newObj, key) => {
        newObj[key] = override ? object[key] : replaceOrKeepValue(key);
        return newObj;
      }, {})
    );
  };

  const loadYourCover = (object, override) => {
    const replaceOrKeepValue = key =>
      key in object && object[key] !== '' ? object[key] : yourCoverObj[key];
    setYourCover(
      Object.keys(yourCoverObj).reduce((newObj, key) => {
        newObj[key] = override ? object[key] : replaceOrKeepValue(key);
        return newObj;
      }, {})
    );
  };

  const loadYourHome = (object, override) => {
    const replaceOrKeepValue = key =>
      key in object && object[key] !== '' ? object[key] : yourHomeObj[key];
    setYourHome(
      Object.keys(yourHomeObj).reduce((newObj, key) => {
        newObj[key] = override ? object[key] : replaceOrKeepValue(key);
        return newObj;
      }, {})
    );
  };

  const loadQuoteSummaryValues = (quote) => {
    saveInSessionStorage(HOME_QUOTE_THANK_YOU_STORAGE, JSON.stringify(quote));
    loadAboutYou(quote.aboutYou, true);
    loadYourCover(quote.yourCover, true);
    loadYourHome(quote.yourHome, true);
  };

  const getQuoteDataForAgent = async (emailParam, policyCodeParam) => {
    setIsLoading(true);
    saveInSessionStorage(HOME_RESUME_QUOTE, false);

    const response = await getLatestUnpurchasedHomeQuote(props, emailParam, policyCodeParam);
    if (Object.keys(response).length) {
      setQuoteDetails(response);
      loadQuoteSummaryValues(response);
      saveInSessionStorage(HOME_QUOTE_STORAGE, JSON.stringify(response));
      const startDate = response?.yourCover?.policyStartDate ? response?.yourCover?.policyStartDate : undefined;
      if (startDate) {
        const [day, month, year] = startDate.split('/').map(Number);
        const dateConverted = new Date(year, month - 1, day).setHours(0, 0, 0, 0);
        const today = new Date().setHours(0, 0, 0, 0);
        const invalidPolicyStart = dateConverted < today;
        setInvalidStartDate(invalidPolicyStart);
      }
    }

    setIsLoading(false);
  };

  const handleAcceptAndContinue = async () => {
    setIsLoading(true);
    const response = await getRequestBuilder('/home/search/latest/quotes', { policy_code: quoteDetails.policy_code });
    if (response?.error) {
      // eslint-disable-next-line no-console
      console.log('Error getting InsurSmart Quote');
    } else {
      const extraQuestionsValues = {
        listed_or_protected:
        quoteDetails.yourCover.listedOrProtected,
        operational_locks:
        quoteDetails.yourCover.operationalLocks,
        roof_tile: quoteDetails.yourCover.roofTile,
        un_occupied: quoteDetails.yourCover.unOccupied
      };
      const toggleOptionalExtras = {
        accidentalDamage: quoteDetails.yourCover.contentsAccidentalDamageCover,
        ncdProtectionPlus: quoteDetails.yourCover.noClaimDiscountCover,
        homeEmergencyAssist: quoteDetails.yourCover.homeEmergencyAssistCover,
        excess: EXCESS_AMOUNT_MAPPER_REVERSE[quoteDetails.yourCover.excessAmount],
        insurerType: ''
      };
      const value = {
        aboutYouObj: aboutYouObj,
        yourHomeObj: yourHomeObj,
        yourCoverObj: {
          ...yourCoverObj,
          policyStart: getPolicyStart(yourCoverObj.policyStartDate)
          //insureSmart: 
        },
        assumptions: quoteDetails.assumptions,
        extraQuestionsValues: extraQuestionsValues
      };
      saveInSessionStorage(HOME_RESUME_QUOTE, true);
      saveInSessionStorage(LAST_PAGE_NAME, JSON.stringify('Summary'));
      saveInSessionStorage(HOME_RESUME_QUOTE_OBJ, JSON.stringify(value));
      saveInSessionStorage(HOME_QUOTE_THANK_YOU_STORAGE, JSON.stringify(value));
      saveInSessionStorage(`${AGENT_SUMMARY_QUOTE_STORAGE}`, JSON.stringify(response));
      saveInSessionStorage(TOGGLE_OPTIONAL_EXTRAS, JSON.stringify(toggleOptionalExtras));

      const linkAddress = `${routes.viewMultipleQuotes.url}home`;
      setIsLoading(false);

      [
        `${routes.getQuotePage.url}aboutYou`,
        `${routes.getQuotePage.url}yourHome`,
        `${routes.getQuotePage.url}yourCover`,
        `${routes.termsAndConditions.url}home`,
        linkAddress
      ].forEach(history.push);
    }
  };

  const renderContinueButton = () => (
    <div className={`${className}__button`}>
      <a
        data-ga
        id={`QuoteSummary__button`}
        data-testid={`QuoteSummary__button`}
      >
        <Button
          quaternary
          fluid
          disabled={invalidStartDate}
          onClick={async () => {
            if (!isValid) {
              submitForm();
            } else {
              handleAcceptAndContinue();
            }
          }}
        >
          Accept and Continue
        </Button>
      </a>
    </div>
  );

  const renderBackToHomeButton = () => (
    <div className={`${className}__backToHomeButton`}>
      <span className={`${className}__boundMessage`}>
        Policy Status : Successfully bound
      </span>
      <a
        data-ga
        id={`QuoteSummary__renderBackToHomeButton`}
        data-testid={`QuoteSummary__renderBackToHomeButton`}
      >
        <Button
          quaternary
          fluid
          onClick={async () => {
            history.push(`${routes.wallet.url}`);
          }}
        >
          Back to Home
        </Button>
      </a>
    </div>
  );

  const renderEditButton = () => (
    <div className={`${className}__backButton`}>
      <a
        data-ga
        id={`QuoteSummary__editQuoteButton`}
        data-testid={`QuoteSummary__editQuoteButton`}
      >
        <Button
          quaternary
          fluid
          onClick={async () => {
            saveInSessionStorage(LAST_PAGE_NAME, JSON.stringify('Summary'));
            saveInSessionStorage(ITB_VALUE, JSON.stringify(yourCoverObj.itb));
            removeFromSessionStorage(HOME_QUOTE_THANK_YOU_STORAGE);
            setIsLoading(true);
            await getLatestQuoteForPolicyCode(quoteDetails.policy_code);
            setIsLoading(false);
            history.push(`${routes.getQuotePage.url}${ABOUT_YOU}`);
          }}
        >
          Edit quote
        </Button>
      </a>
    </div>
  );

  const propertyAddress = () => {
    return `${aboutYouObj.addressLine1} ${aboutYouObj.addressLine2} ${aboutYouObj.address_line3 ?? ''} ${aboutYouObj.address_line4 ?? ''} ${aboutYouObj.town ?? ''} ${aboutYouObj.county}`;
  };

  const getFormStatus = () => {
    return isValid ? 'success' : 'warning';
  };

  const getMortgageProvider = (value) => {
    const foundItem = mortgageProviders.find(item => item.dropdownValue === Number(value));
    return foundItem ? foundItem.displayName : `Unknown Mortgage Provider (Code: ${value})`;
  };

  const yourCoverSummaryRows = () => {
    return [
      {
        label: 'Policy Start Date',
        value: yourCoverObj.policyStartDate,
        unit: ''
      },
      {
        label: 'Buildings Cover',
        value: getFormattedCurrencyNumber(getMonetaryValue(yourCoverObj.rebuildCost))
      },
      {
        label: 'Contents Cover',
        value: getFormattedCurrencyNumber(getMonetaryValue(yourCoverObj.contentsCoverOf))
      },
      {
        label: 'Unspecified Items',
        value: getFormattedCurrencyNumber(getMonetaryValue(yourCoverObj.unspecifiedItems))
      },
      {
        label: 'Specified Items',
        value: getFormattedCurrencyNumber(getMonetaryValue(yourCoverObj.specifiedItems.reduce((sum, item) => sum + item.value, 0)))
      },
      {
        label: 'Policy Excess',
        value: getFormattedCurrencyNumber(getMonetaryValue(yourCoverObj.excessAmount))
      },
      {
        label: 'Contents Accidental Damage',
        value: `${isTrue(yourCoverObj.contentsAccidentalDamageCover) ? 'Yes' : 'No'}`
      },
      {
        label: 'No Claim Discount Protection',
        value: `${isTrue(yourCoverObj.noClaimDiscountCover) ? 'Yes' : 'No'}`
      },
      {
        label: 'Home Emergency Assist Cover',
        value: `${isTrue(yourCoverObj.homeEmergencyAssistCover) ? 'Yes' : 'No'}`
      }
    ];
  };

  const aboutYouSummaryRows = () => {
    let items = [
      {
        label: 'Name',
        value: `${getTitle(aboutYouObj.title)} ${aboutYouObj.firstName} ${aboutYouObj.lastName}`
      },
      {
        label: 'Date or Birth',
        value: aboutYouObj.dob
      }
    ];

    if (Object.keys(aboutYouObj.jointlyOwnedDetails).length > 0) {
      items = [...items, ...[
        {
          label: 'Joint Owner',
          value: `${getTitle(aboutYouObj.jointlyOwnedDetails.title)} ${aboutYouObj.jointlyOwnedDetails.firstName} ${aboutYouObj.jointlyOwnedDetails.lastName}`
        },
        {
          label: 'Joint Date of Birth',
          value: aboutYouObj.jointlyOwnedDetails.dateOfBirth
        }
      ]];
    }

    items = [...items, ...[
      {
        label: 'Email Address',
        value: aboutYouObj.email
      },
      {
        label: 'Mobile Phone Number',
        value: aboutYouObj.phoneNo
      },
      {
        label: 'First Time Buyer',
        value: `${isTrue(aboutYouObj.firstTimeBuyer) ? 'Yes' : 'No'}`
      },
      {
        label: 'Switching Mortgage',
        value: `${isTrue(aboutYouObj.switcherMortgage) ? 'Yes' : 'No'}`
      },
      {
        label: 'Mortgage Application Number',
        value: aboutYouObj.mortgageApplicationNumber
      },
      {
        label: 'Mortgage provider',
        value: getMortgageProvider(aboutYouObj.mortgageProvider)
      },
      {
        label: 'Promocode',
        value: aboutYouObj.promoCode
      }
    ]];

    return items;
  };

  const yourHomeSummaryRows = () => {
    return [
      {
        label: 'Property Address',
        value: propertyAddress()
      },
      {
        label: 'Property Ownership',
        value: getOwnership(yourHomeObj.coverType)
      },
      {
        label: 'Property Type',
        value: yourHomeObj.homeType
      },
      {
        label: 'Year Built',
        value: yourHomeObj.yearOfConstruction
      },
      {
        label: 'Number of Bedrooms',
        value: yourHomeObj.numBedrooms
      },
      {
        label: 'Number of Bathrooms',
        value: yourHomeObj.numBathrooms
      },
      {
        label: 'Source of Heating',
        value: yourHomeObj.heatingSource
      },
      {
        label: 'Burglar Alarm',
        value: yourHomeObj.alarmType
      },
      {
        label: 'Number of Smoke Detectors',
        value: yourHomeObj.numberOfSmokeDetectors
      },
      {
        label: 'Claims Free (years)',
        value: yourHomeObj.noClaimYears >= 0 ? yourHomeObj.noClaimYears : 'No previous insurance'
      },
      {
        label: 'Claim History',
        value: `${isTrue(yourHomeObj.claimsValue) ? 'Yes' : 'No'}`
      }
    ];
  };

  const assumptionsSummaryRows = () => {
    return [
      {
        value: 'all external doors and windows of this property, including domestic outbuildings, are securely fitted with fully operational locks'
      },
      {
        value: 'the external walls of this property are built entirely with stone, brick or concrete'
      },
      {
        value: 'your roof is entirely covered in Tile, Slate, Concrete, Metal (other than corrugated iron) or Asphalt'
      },
      {
        value: 'this property is structurally sound, weatherproof, free from damage or defect of any kind, and will it continue to be maintained in a good state of repair'
      },
      {
        value: 'this property will not be left unoccupied for 45 consecutive days or more in any one year'
      },
      {
        value: 'this property has not been divided into separate living units or is not a flat or a bedsit'
      },
      {
        value: 'no part of this property is a listed or protected structure'
      },
      {
        value: 'neither you or any member of your household have ever been convicted of an offence, or have a prosecution pending, other than speeding or parking offences  (You do not need to tell us about any spent convictions)'
      },
      {
        value: 'neither you or any member of your household have ever been refused insurance or had insurance cancelled or special terms or conditions imposed by any insurer (for example. Increased excesses or policy loadings)'
      },
      {
        value: 'no more than 30% of your roof is flat'
      },
      {
        value: 'this property is currently not under construction, renovation, extension, repair or redecoration (excluding minor internal decoration work such as painting and wallpapering)'
      },
      {
        value: 'no part of this property, including any outbuildings, is being used for business purposes or to store commercial goods'
      },
      {
        value: 'this property has never been underpinned or required structural support, nor has it ever been the subject of a survey which mentions settlement or movement'
      },
      {
        value: 'you are unaware if this property, its site or the surrounding area (within 200 meters) has shown any signs of or been monitored for subsidence, landslip, ground heave, landslide, coastal or river erosion'
      },
      {
        value: 'this property has never flooded.  You must tell us if this property including any outbuildings, garden or land, has any history of flooding (from external sources such as sea, river, lakes or rainfall)'
      }
    ];
  };


  const getOwnership = (type) => HOME_TYPE_VALUE[type] || 'Unknown';

  const getTitle = (titleOption) => {
    const index = OPTION_TITLE_VALUES_CONSTANT.indexOf(titleOption);
    return index !== -1 ? TITLES_CONSTANT[index] : 'Unknown';
  };

  const assumptionsConfirmationSection = () => {
    return (
      <div
        className={`${commonFormStylesIdentifier}__fieldContainer ${commonFormStylesIdentifier}--withNoMargin  ${commonFormStylesIdentifier}--withMargi/nTop`}
      >
        <label
          htmlFor="areAssumptionsCorrect"
          className={`${commonFormStylesIdentifier}__fieldLabel`}
        >
          Are all these assumptions correct?
        </label>
        <span className={`${className}__input`}>
          <div className={`${className}--fieldLabelSpacing`}>
            <ButtonGroup
              name="areAssumptionsCorrect"
              onClick={(value) => {
                setFieldValue('areAssumptionsCorrect', value === 'true');
              }}
              options={[
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' }
              ]}
              touched={touched.areAssumptionsCorrect}
              selected={values.areAssumptionsCorrect}
              error={isFieldError('areAssumptionsCorrect', touched, errors)}
              errorMessage={errors.areAssumptionsCorrect}
              disabled={values.itbQuoteAssumptions}
            />
          </div>
        </span>
      </div>
    );
  };

  const aboutYouSection = () => {
    return (
      <SummaryAccordion
        id="AboutYou"
        label="About You"
        rows={aboutYouSummaryRows()}
      />
    );
  };

  const yourHomeSection = () => {
    return (
      <SummaryAccordion
        id="YourHome"
        label="Your Home"
        rows={yourHomeSummaryRows()}
      />
    );
  };

  const yourCoverSection = () => {
    return (
      <SummaryAccordion
        id="YourCover"
        label="Your Cover"
        rows={yourCoverSummaryRows()}
      />
    );
  };

  const assumptionsSection = () => {
    return (
      <SummaryAccordion
        id="Assumptions"
        label="Assumptions"
        type="list"
        heading="Your quote is based on the following assumptions. If there is any change in this information, or you are in any doubt about our questions and your responses, you must tell us by contacting Bank if Ireland Insurance Services."
        rows={assumptionsSummaryRows()}
        status={getFormStatus()}
      >
        {assumptionsConfirmationSection()}
      </SummaryAccordion>
    );
  };


  function getInsurerDisplay(value) {
    if (value === 'FBD') return 'FBD';
    if (value === 'RSA') return 'Intact';
    return 'Unspecified';
  }

  const getCurrencyPremium = (premium) => {
    const monetaryValue = getMonetaryValue(premium);
    return getFormattedDecimalNumber(monetaryValue);
  };

  const showInsurerQuote = isAgent() && yourCoverObj.itb === true && getItemFromLocalStorage('INSURER_SPECIFIED') === JSON.stringify('SPECIFIED');

  function boundQuote(quoteDetails) {
    return isNotEmpty(quoteDetails?.policy_number) || isNotEmpty(quoteDetails?.payment_channel);
  }

  return (
    <>
      {(quoteDetails && !isLoading) && (
        <div className={className}>
          <div className={`${commonPageStylesIdentifier}__hideOnDesktop`}>
            <MobileBackButton history={history} />
          </div>
          <div className={`${className}__pageTitle`}>
            <Title align="left" style={{ float: 'left' }}>Quote Reference: {aboutYouObj.policyCode} </Title>
            <Title align="right" style={{ float: 'right' }}>Policy Start Date: {yourCoverObj.policyStartDate} </Title>
          </div>
          <div className={`${className}__pageTitle`}>
            <Title align="left" style={{ float: 'left' }}>Selected
              Insurer: {showInsurerQuote ? (getInsurerDisplay(yourCoverObj.carrier_id)) : 'Unspecified'} </Title>
            <Title align="right"
                   style={{ float: 'right' }}>Premium: {showInsurerQuote ? getCurrencyPremium(yourCoverObj.premium) : '-'} </Title>
          </div>
          <div className={`${className}__summaryAccordions`}>
            <div>
              <div className={`${className}__accordions`}>
                {aboutYouSection()}
                <br />
                {yourHomeSection()}
                <br />
                {yourCoverSection()}
                <br />
                {assumptionsSection()}
              </div>
              <div className={`${className}__buttonContainer`}>
                {boundQuote(quoteDetails) ? renderBackToHomeButton() : (<>{renderEditButton()}
                  {renderContinueButton()}</>)}
              </div>

              {invalidStartDate && (
                <div className={`${className}__errorMessage`}>
                  Policy Start Date must be today or in the future.
                  Please click “Edit quote” to return to quote details and amend Policy Start Date
                </div>)}
            </div>
          </div>
        </div>
      )}
      {isLoading && (
        <div className={`${className}__spinnerContainer`}>
          <AnimatedSpinner size="large" />
        </div>
      )}
    </>

  );
};

const errorMessageNotSelected = 'Please provide an answer in relation to the Assumptions';
const errorMessageNoSelected = 'Please edit the quote and review all answers and Assumptions before continuing';

const FormikQuoteSummary = withFormik({
  mapPropsToValues: () => ({
    areAssumptionsCorrect: null
  }),
  validationSchema: yup.object().shape({
    areAssumptionsCorrect: yup
      .boolean()
      .when('itbQuoteAssumptions', {
        is: true,
        then: yup.boolean(),
        otherwise: yup.boolean()
          .nullable()
          .required(errorMessageNotSelected)
          .test('is-true',
            errorMessageNoSelected,
            value => {
              return value === null || value === true;
            })
      }),
    itbQuoteAssumptions: yup.boolean().required()
  }),
  displayName: 'HomeQuoteSummary'
})(HomeQuoteSummaryPage);

HomeQuoteSummaryPage.propTypes = {
  history: PropTypes.any,
  setFieldValue: PropTypes.func,
  submitForm: PropTypes.func,
  setFieldTouched: PropTypes.func,
  errors: PropTypes.object,
  values: PropTypes.object,
  touched: PropTypes.object,
  isValid: PropTypes.bool
};

export default FormikQuoteSummary; 
